import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import API from "../../../Api/Api";
import NEW_API from "../../../Api/NewApi";
import { API_END_POINT } from "../../../Api/EndPoints";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import QuotePill, { QUOTE_STATES, quoteStateOf } from "./QuotePill";
import CreateOrderModal from "./CreateOrderModal";
import EditQuoteModal from "./EditQuoteModal";
import AcceptOfflineModal from "./AcceptOfflineModal";
import CancelSaleModal from "./CancelSaleModal";
import ModifyOrder from "../../Commandes/ModifyOrder/ModifyOrder";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import { getModels } from "../../../REDUX/actions/model";
import {
  saveProjectQuote,
  deleteProjectQuote,
  downloadQuotePdf,
  viewQuotePdf,
  sendProjectQuote,
  duplicateProjectQuote,
} from "../../../REDUX/actions/appProjects";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import useCanSeePrices, { useCanOrder } from "../../../hooks/useCanSeePrices";

const TEAL = "#14748e";
const formatEuro = (value) =>
  `${(Number(value) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

// Builder sections — every line is Choix produit | Prix (€, prefilled with
// the catalogue price, editable) | Remise (%). Produit libre = ANY product;
// delivery ("Frais") has no catalogue price.
const SECTIONS = [
  { key: "product", title: "Add a product", heading: null, types: [1] },
  { key: "accessory", title: "Add an accessory", heading: null, types: [2] },
  {
    key: "freeProduct",
    title: "Add an open product",
    heading: null,
    types: [1, 2, 3, 4, 5, 6],
  },
  {
    key: "delivery",
    title: "Add a delivery",
    heading: "Fees",
    types: "delivery",
  },
  {
    key: "installation",
    title: "Add an installation",
    heading: null,
    types: [5],
  },
];

let lineKey = 0;
const newLine = (section) => ({
  key: `line-${lineKey++}`,
  section,
  productId: null,
  label: "",
  unitPrice: 0,
  remise: 0,
});
const lineTotal = (line) =>
  (Number(line.unitPrice) || 0) * (1 - (Number(line.remise) || 0) / 100);

/**
 * Devis tab: existing quotes first (newest on top — edit / PDF / delete),
 * then the builder: produits, accessoires, produit libre, Frais (livraison),
 * installation, prospect to send to, total.
 */
const ProjectQuotes = ({ project, sale }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const { updateLoading } = useSelector((state) => state.appProjectsReducer);
  const { models, informations } =
    useSelector((state) => state.modelReducer) ?? {};
  // Quote lines keep the French product name (PDF, app, order), whatever
  // language SOFT is displayed in.
  const frenchName = (productId) =>
    (informations ?? []).find((row) => row.id === productId)?.attributes?.name;

  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [builder, setBuilder] = useState({ id: null, lines: [] });
  // "Validité : jusqu'au" — empty = 30 days, set by the API when sent.
  const [validUntil, setValidUntil] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    if (!models?.length) {
      dispatch(getModels(i18n.language?.includes("en") ? "en" : "fr"));
    }
    (async () => {
      try {
        const [types, custs] = await Promise.all([
          API.get(`${API_END_POINT}api/delivery-types`, {
            signal: controller.signal,
          }),
          NEW_API.get("/api/customers", {
            params: { page: 1, pageSize: 1000 },
            signal: controller.signal,
          }),
        ]);
        setDeliveryTypes(
          (types.data?.data ?? []).map((row) => ({
            name: row.attributes?.name,
            code: row.id,
          })),
        );
        const options = (custs.data?.data ?? []).map((row) => ({
          name:
            `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() ||
            row.username ||
            row.email,
          code: row.id,
        }));
        setCustomers(options);
        // Default recipient: the project's main contact.
        const main = options.find(
          (option) => option.code === project.mainContact?.id,
        );
        if (main) setRecipient(main);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quotes = project.quotes ?? [];
  const modelPrice = (model) =>
    model?.publicPriceTTC ?? model?.retailerPriceHT ?? 0;
  const optionsFor = (types) =>
    types === "delivery"
      ? deliveryTypes
      : (models ?? [])
          .filter((model) => types.includes(model.productType))
          .map((model) => ({
            name: `${model.name} — ${formatEuro(modelPrice(model))}`,
            code: model.code,
          }));

  const setLine = (key, patch) =>
    setBuilder((prev) => ({
      ...prev,
      lines: prev.lines.map((line) =>
        line.key === key ? { ...line, ...patch } : line,
      ),
    }));
  const removeLine = (key) =>
    setBuilder((prev) => ({
      ...prev,
      lines: prev.lines.filter((line) => line.key !== key),
    }));

  const total = builder.lines.reduce((sum, line) => sum + lineTotal(line), 0);

  // Contrôle de saisie: a row left without a product is simply not saved (no
  // installation / produit libre forced). A chosen line needs a price ≥ 0
  // (0 € = "Offert") and a remise between 0 and 100.
  const isLineValid = (line) => {
    const remise = Number(line.remise) || 0;
    return Number(line.unitPrice) >= 0 && remise >= 0 && remise <= 100;
  };
  const filledLines = builder.lines.filter((line) => line.productId);
  const hasInvalidLines = filledLines.some((line) => !isLineValid(line));

  // Marge % = (selling − cost) / selling, cost being the model's retailer
  // price for lines tied to a product (libre/delivery lines count as cost 0).
  const totalCost = builder.lines.reduce((sum, line) => {
    const model = (models ?? []).find((m) => m.code === line.productId);
    return sum + (Number(model?.retailerPriceHT) || 0);
  }, 0);
  const margeRate =
    total > 0 ? Math.round(((total - totalCost) / total) * 1000) / 10 : 0;
  const canSeePrices = useCanSeePrices();
  const canOrder = useCanOrder(); // a commercial: the store places the order

  const loadQuote = (quote) => {
    setBuilder({
      id: quote.id,
      lines: (quote.lines ?? []).map((line) => ({
        ...newLine(line.section),
        ...line,
      })),
    });
    // A past validity can't be picked again (CalendarInput): +30 days.
    setValidUntil(
      quote.validUntil && moment(quote.validUntil).isSameOrAfter(moment(), "day")
        ? new Date(quote.validUntil)
        : null,
    );
  };
  const resetBuilder = () => {
    setBuilder({ id: null, lines: [] });
    setValidUntil(null);
  };

  const handleSave = (recipientId = null) => {
    dispatch(
      saveProjectQuote(
        project.id,
        {
          quoteId: builder.id,
          lines: filledLines.map(({ key, ...line }) => line),
          recipientId,
          validUntil: validUntil
            ? moment(validUntil).format("YYYY-MM-DD")
            : undefined,
        },
        t,
        resetBuilder,
      ),
    );
  };

  const priceInput = (line, name, label) => (
    <div className="col-2" key={name}>
      <label className="f13 text-brightGrey mb-1">{t(label)}</label>
      <TextInput
        type="number"
        min={0}
        max={name === "remise" ? 100 : undefined}
        value={line[name]}
        callback={(e) => setLine(line.key, { [name]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="flex-1 d-flex flex-column">
      <div className="modalContent px-5 pt-4 pb-5">
        {/* Existing quotes — newest first */}
        <h4 className="f16 bold quickSand text-blue mb-3">{t("Quotes")}</h4>
        <div className="pb-4 mb-4 border-bottom">
          {quotes.length === 0 && (
            <p className="f14 text-brightGrey mb-0">{t("No quotes yet")}</p>
          )}
          {quotes.map((quote) => {
            const state = quoteStateOf(quote);
            // The store's order to Poseidon for this quote's sale (its cart).
            const order = sale && quote.order?.id === sale.id ? sale.cart : null;
            // Editable until an order exists; deletable until one ever did
            // (a cancelled order keeps its quote).
            const liveOrder =
              !!quote.order && quote.order.state !== "cancelled";
            const anyOrder = !!quote.order;
            return (
              <div
                key={quote.id}
                className="d-flex align-items-center gap-3 py-2 px-3 br10 b-1-lightGrey bg-white mb-2"
              >
                <i
                  className="bi bi-file-earmark-text"
                  style={{ color: TEAL }}
                />
                <span className="f14 sBold text-ultraBlack text-nowrap">
                  {t("Quote")} n°{quote.id}
                </span>
                <span className="f13 text-brightGrey">
                  {moment(quote.createdAt).format("DD/MM/YYYY")}
                </span>
                {state === "accepted" && order && (
                  <QuotePill
                    tone="chip"
                    onClick={() =>
                      dispatch(openRightModal(<ModifyOrder id={order.id} />))
                    }
                  >
                    {t("Order no. {{number}} · {{status}}", {
                      number: order.number,
                      status: t(`${order.status}`),
                    })}
                  </QuotePill>
                )}
                {quote.type === "app" && (
                  <span
                    className="f12 sBold px-2 py-1 br10"
                    style={{ color: TEAL, background: "#e7f2f5" }}
                  >
                    {t("From the app")}
                  </span>
                )}
                <span
                  className="f14 sBold ms-auto text-nowrap"
                  style={{ color: TEAL }}
                >
                  {formatEuro(quote.totalAmount)}
                </span>
                <QuotePill tone={QUOTE_STATES[state]?.tone}>
                  {t(QUOTE_STATES[state]?.label ?? "Draft")}
                </QuotePill>
                {state === "accepted_by_customer" && canOrder && (
                  <Button
                    onClick={() =>
                      dispatch(
                        openCenteredModal(
                          <CreateOrderModal project={project} quote={quote} />,
                        ),
                      )
                    }
                  >
                    {t("Place the order")}
                  </Button>
                )}
                {state === "unavailable" && (
                  <Button
                    onClick={() =>
                      dispatch(
                        openCenteredModal(
                          <EditQuoteModal
                            project={project}
                            quote={quote}
                            onOtherModel={() => loadQuote(quote)}
                          />,
                        ),
                      )
                    }
                  >
                    {t("Edit the quote")}
                  </Button>
                )}
                {["refused", "expired"].includes(state) && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      dispatch(duplicateProjectQuote(project.id, quote.id, t))
                    }
                  >
                    {t("Duplicate the quote")}
                  </Button>
                )}
                <VerticalPoints
                  showLeft
                  options={[
                    {
                      text: t("Edit"),
                      hidden: liveOrder,
                      // Editing cancels the customer's acceptance: confirm.
                      callback: () =>
                        state === "accepted_by_customer"
                          ? dispatch(
                              openCenteredModal(
                                <ConfirmModal
                                  text={t(
                                    "{{name}} accepted this quote. Editing it cancels that acceptance and {{name}} will have to accept it again. Continue",
                                    {
                                      name: [
                                        project.mainContact?.firstName,
                                        project.mainContact?.lastName,
                                      ]
                                        .filter(Boolean)
                                        .join(" "),
                                    },
                                  )}
                                  confirmText={t("Edit")}
                                  onConfirm={() => loadQuote(quote)}
                                />,
                              ),
                            )
                          : loadQuote(quote),
                    },
                    {
                      text: t("Send the quote"),
                      hidden:
                        liveOrder ||
                        !["draft", "sent", "refused", "expired"].includes(
                          state,
                        ),
                      callback: () =>
                        dispatch(sendProjectQuote(project.id, quote.id, t)),
                    },
                    {
                      text: t("Mark as accepted outside the app"),
                      hidden: state !== "sent",
                      callback: () =>
                        dispatch(
                          openCenteredModal(
                            <AcceptOfflineModal
                              projectId={project.id}
                              quoteId={quote.id}
                            />,
                          ),
                        ),
                    },
                    {
                      text: t("View the quote"),
                      callback: () => dispatch(viewQuotePdf(quote.id, t)),
                    },
                    {
                      text: t("Download PDF"),
                      callback: () => dispatch(downloadQuotePdf(quote.id, t)),
                    },
                    {
                      text: t("Cancel the order"),
                      hidden:
                        !liveOrder ||
                        quote.order?.state === "delivered" ||
                        quote.order?.id !== sale?.id,
                      red: true,
                      callback: () =>
                        dispatch(
                          openCenteredModal(
                            <CancelSaleModal
                              saleId={quote.order.id}
                              projectId={project.id}
                            />,
                          ),
                        ),
                    },
                    {
                      text: t("Delete"),
                      hidden: anyOrder,
                      red: true,
                      callback: () =>
                        dispatch(
                          openCenteredModal(
                            <ConfirmModal
                              id={quote.id}
                              label={t("Quote")}
                              onConfirm={() =>
                                dispatch(
                                  deleteProjectQuote(project.id, quote.id, t),
                                )
                              }
                            />,
                          ),
                        ),
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>

        {/* Builder */}
        <h4 className="f16 bold quickSand text-blue mb-3">
          {builder.id
            ? `${t("Edit")} — ${t("Quote")} n°${builder.id}`
            : t("New quote")}
        </h4>
        {SECTIONS.map(({ key, title, heading, types }) => {
          const sectionLines = builder.lines.filter(
            (line) => line.section === key,
          );
          const options = optionsFor(types);
          return (
            <div className="pb-4 mb-4 border-bottom" key={key}>
              {heading && (
                <h4 className="f16 bold quickSand text-blue mb-3">
                  {t(heading)}
                </h4>
              )}
              <p className="f14 sBold text-ultraBlack mb-2">{t(title)}</p>
              {sectionLines.map((line) => (
                <div className="row g-3 align-items-end mb-2" key={line.key}>
                  <div className="col-5">
                    <Select
                      appendToSelf
                      filter
                      placeholder={t(title)}
                      options={options}
                      value={
                        options.find((o) => o.code === line.productId) ?? null
                      }
                      callback={(e) => {
                        const option = e.value;
                        if (types === "delivery") {
                          setLine(line.key, {
                            productId: option?.code ?? null,
                            label: option?.name ?? "",
                          });
                        } else {
                          const model = (models ?? []).find(
                            (m) => m.code === option?.code,
                          );
                          setLine(line.key, {
                            productId: option?.code ?? null,
                            label: frenchName(option?.code) ?? model?.name ?? "",
                            unitPrice: modelPrice(model),
                          });
                        }
                      }}
                    />
                  </div>
                  {priceInput(line, "unitPrice", "Price (€)")}
                  {priceInput(line, "remise", "Discount (%)")}
                  <div className="col-2 pb-2 f14 sBold text-ultraBlack text-end">
                    {formatEuro(lineTotal(line))}
                  </div>
                  <div className="col-1 pb-2">
                    <button
                      type="button"
                      className="border-0 bg-transparent text-red-600 f16"
                      onClick={() => removeLine(line.key)}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="d-flex align-items-center gap-2 border-0 bg-transparent f14 sBold mt-2"
                style={{ color: TEAL }}
                onClick={() =>
                  setBuilder((prev) => ({
                    ...prev,
                    lines: [...prev.lines, newLine(key)],
                  }))
                }
              >
                <span
                  className="d-flex align-items-center justify-content-center br50 text-white"
                  style={{ width: 28, height: 28, backgroundColor: TEAL }}
                >
                  <i className="bi bi-plus-lg" />
                </span>
                {t(title)}
              </button>
            </div>
          );
        })}

        {/* Send to */}
        <div className="pb-4 mb-4 border-bottom">
          <h4 className="f16 bold quickSand text-blue mb-3">{t("Send to")}</h4>
          <div className="d-flex align-items-center gap-3">
            <div className="max-w-300px w-100">
              <Select
                filter
                showClear
                resetable
                placeholder={t("Prospect")}
                options={customers}
                value={recipient}
                callback={(e) => setRecipient(e.value ?? null)}
              />
            </div>
            <span className="f14 sBold text-ultraBlack text-nowrap">
              {t("Valid until")}
            </span>
            <div style={{ width: 170 }}>
              <CalendarInput
                value={validUntil}
                callback={(e) => setValidUntil(e.value ?? null)}
              />
            </div>
            <Button
              loading={updateLoading}
              disabled={!recipient || !filledLines.length || hasInvalidLines}
              Icon={() => <i className="bi bi-send" />}
              onClick={
                updateLoading ? undefined : () => handleSave(recipient?.code)
              }
            >
              {t("Send the quote")}
            </Button>
          </div>
        </div>

        {/* Total */}
        <div
          className="d-flex align-items-center justify-content-between px-4 py-3 br10 text-white mb-4"
          style={{ backgroundColor: TEAL }}
        >
          <span className="f15 sBold">{t("Project total")}</span>
          <span className="text-end">
            <span className="d-block f18 bold">{formatEuro(total)}</span>
            {/* A store's commercial sees no margin. */}
            {canSeePrices && (
              <span className="d-block f13">
                {t("Margin rate")} : {margeRate}%
              </span>
            )}
            {/* <span className="d-block f12">{t("(excl. transport, VAT)")}</span> */}
          </span>
        </div>

        {hasInvalidLines && (
          <p className="f13 text-red-600 text-center mb-3">
            {t("Check the prices and discounts of the quote lines")}
          </p>
        )}
        <div className="d-flex justify-content-center gap-3">
          {builder.id && (
            <Button variant="outline" onClick={resetBuilder}>
              {t("New quote")}
            </Button>
          )}
          <Button
            loading={updateLoading}
            disabled={!filledLines.length || hasInvalidLines}
            onClick={updateLoading ? undefined : () => handleSave()}
          >
            {t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectQuotes;
